import React, { useEffect, useRef } from "react";

interface ConfettiProps {
  trigger: boolean;
  onComplete?: () => void;
}

const Confetti: React.FC<ConfettiProps> = ({ trigger, onComplete }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const isAnimatingRef = useRef<boolean>(false);

  useEffect(() => {
    // Prevent restart if already animating or trigger is false
    if (!trigger || !canvasRef.current || isAnimatingRef.current) return;

    // Honor reduced-motion: skip the celebration animation entirely and signal
    // completion so the surrounding flow proceeds unaffected (WCAG 2.3.3).
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      if (onComplete) onComplete();
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Mark as animating
    isAnimatingRef.current = true;

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Confetti particle class
    class ConfettiParticle {
      x: number;
      y: number;
      size: number;
      speedY: number;
      speedX: number;
      color: string;
      rotation: number;
      rotationSpeed: number;
      opacity: number;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = -10;
        this.size = Math.random() * 8 + 4;
        this.speedY = Math.random() * 3 + 2;
        this.speedX = Math.random() * 2 - 1;
        this.color = this.getRandomColor();
        this.rotation = Math.random() * 360;
        this.rotationSpeed = Math.random() * 10 - 5;
        this.opacity = 1;
      }

      getRandomColor(): string {
        const colors = [
          "#7551D0", // Purple
          "#47AA12", // Green
          "#FF6B6B", // Red
          "#4ECDC4", // Cyan
          "#FFD93D", // Yellow
          "#FF8E53", // Orange
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      }

      update() {
        this.y += this.speedY;
        this.x += this.speedX;
        this.rotation += this.rotationSpeed;

        // Fade out as it falls
        if (this.y > canvas.height * 0.7) {
          this.opacity -= 0.02;
        }
      }

      draw(ctx: CanvasRenderingContext2D) {
        ctx.save();
        ctx.globalAlpha = this.opacity;
        ctx.translate(this.x, this.y);
        ctx.rotate((this.rotation * Math.PI) / 180);
        ctx.fillStyle = this.color;
        ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
        ctx.restore();
      }

      isOffScreen(): boolean {
        return this.y > canvas.height || this.opacity <= 0;
      }
    }

    // Create confetti particles
    const particles: ConfettiParticle[] = [];
    const particleCount = 1000;

    for (let i = 0; i < particleCount; i++) {
      setTimeout(() => {
        particles.push(new ConfettiParticle());
      }, i * 20);
    }

    // Animation loop
    let startTime = Date.now();
    const duration = 5000; // 4 seconds

    const animate = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed > duration) {
        isAnimatingRef.current = false;
        if (onComplete) onComplete();
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      for (let i = particles.length - 1; i >= 0; i--) {
        particles[i].update();
        particles[i].draw(ctx);

        if (particles[i].isOffScreen()) {
          particles.splice(i, 1);
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      isAnimatingRef.current = false;
    };
  }, [trigger]); // Keep trigger in dependency but use isAnimatingRef to prevent restart

  if (!trigger) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    />
  );
};

export default Confetti;
